/**
 * step-runner (US-AI.F2-0231 · @UC9400) — chạy Step của flow: điền template (biến ALLOWLIST) + deep-link.
 *
 * @AI-NUM-01 — {soTien} nhận BigInt MINOR và format "500.000 đ" (KHÔNG in raw minor, KHÔNG float).
 * @AI-AMB-16 — biến chỉ nhận giá trị do rule-engine rút từ ctx THẬT; biến ngoài allowlist giữ nguyên
 *              (không thay thế ⇒ không rò dữ liệu ngoài ý muốn).
 * @AI-ONB-09 — {tenNguoiDung} = displayName THẬT; caller đưa "bạn" khi thiếu tên (không suy đoán giới).
 */
import type { Flow, FlowOutput,
  FlowUnit, OrchestrationContext, Step, TemplateVars } from "./types.js";
import {
  buildTemplateVars,
  duesForFlow,
  safeDisplayName,
  selectPrimaryDue,
} from "./rule-vars.js";
import { NOOP_TRACE, type TraceCollector } from "./trace.js";
import { WRITE_DEEPLINK_KEYS, resolveRoute, type DeepLink } from "./route-catalog.js";

/** Biến được phép xuất hiện trong template (allowlist đóng). */
// 🔴 US-AI-0440-S01 (@UC9692) — thêm `tenNhom`. Đây là tên NHÓM (một tổ chức), KHÔNG phải danh tính
// một con người: luật P5.3 cấm nêu **tên người khác** ở kênh đẩy, không cấm gọi tên nhóm mà chính
// người đọc đang ở trong. Không gọi tên thì câu "một nhóm của bạn đang tạm dừng" vô dụng với người
// có nhiều nhóm. Guard `duty-claimed.spec` khoá danh sách này bằng allowlist TƯỜNG MINH.
const ALLOWED_VARS = ["tenQuy", "tenNhom", "soTien", "tenNguoiDung", "tenDot", "soViec"] as const;

/**
 * Allowlist biến template — NGUỒN SỰ THẬT DUY NHẤT (US-AI.F3-0232: admin sửa template qua API dùng
 * CHÍNH danh sách này để validate ⇒ không nhân bản allowlist ở tầng API).
 */
export const TEMPLATE_ALLOWED_VARS: readonly string[] = Object.freeze([...ALLOWED_VARS]);

/** Regex rút mọi placeholder `{ten}` trong template (tên biến: chữ/số/_). */
const PLACEHOLDER_RE = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;

/**
 * Rút danh sách biến LẠ (ngoài allowlist) trong template admin nhập (US-AI.F3-0232 · AC3).
 *
 * Rỗng ⇒ template hợp lệ. Có phần tử ⇒ API PHẢI từ chối 422 và KHÔNG ghi (biến lạ = rò dữ liệu ngoài ý
 * muốn / placeholder chết hiện nguyên văn cho end-user).
 */
export function unknownTemplateVars(template: string): readonly string[] {
  const found = new Set<string>();
  for (const m of template.matchAll(PLACEHOLDER_RE)) {
    const name = m[1]!;
    if (!TEMPLATE_ALLOWED_VARS.includes(name)) found.add(name);
  }
  return Object.freeze([...found]);
}

/**
 * Format tiền VND từ BigInt minor → "1.234.567 đ" (dấu chấm phân nhóm nghìn kiểu VN).
 * KHÔNG dùng Number/float ở đường tiền (ADR-0002) — chỉ thao tác chuỗi trên BigInt.
 */
export function formatVndMinor(amountMinor: bigint): string {
  const neg = amountMinor < 0n;
  const digits = (neg ? -amountMinor : amountMinor).toString();
  let out = "";
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ".";
    out += digits[i];
  }
  return `${neg ? "-" : ""}${out} đ`;
}

/** Đổi 1 giá trị biến → chuỗi hiển thị (tiền qua formatVndMinor — no-float). */
function renderVar(name: (typeof ALLOWED_VARS)[number], vars: TemplateVars): string | null {
  const v = vars[name];
  if (v === undefined || v === null) return null;
  if (typeof v === "bigint") return formatVndMinor(v);
  return String(v);
}

/**
 * Điền template của 1 step. Chỉ thay biến trong ALLOWLIST và CHỈ khi caller cung cấp giá trị;
 * placeholder lạ / thiếu giá trị → giữ nguyên (fail-visible, không im lặng bịa).
 */
export function renderStep(step: Step, template: string, vars: TemplateVars): string {
  if (step.type !== "message") return "";
  return renderTemplateVars(template, vars);
}

/**
 * 🔴 US-AI-0482-S01 (@UC9765) — BỘ ĐIỀN TEMPLATE, TÁCH KHỎI `renderStep`.
 *
 * Vì sao tách: nhãn CTA và câu chip **không** phải câu của một `Step` kiểu `message` (chúng nằm ở
 * `flow.configurable.ctaLabelVi/En` và ở `Step.chipPrompt` của bước `deep_link`), nên gọi
 * `renderStep` cho chúng sẽ trả về chuỗi RỖNG vì vế `step.type !== "message"`. Trước bản vá, hệ quả
 * là hai thứ đó **chưa bao giờ đi qua bộ điền** — lấy nguyên văn từ catalog.
 *
 * 🔴 MỘT bộ điền, KHÔNG hai: allowlist (`ALLOWED_VARS`), luật tiền (`bigint ⇒ formatVndMinor`,
 * no-float) và luật fail-visible (biến thiếu giá trị ⇒ **giữ nguyên placeholder**, không bịa) đều
 * nằm ĐÚNG ở đây. `renderStep` nay chỉ còn là vỏ kiểm loại step. Nhân bản `.replace()` ra call-site
 * là cách allowlist trở nên vô nghĩa — đúng mẫu hình "cơ chế thứ hai" mà repo cấm.
 */
export function renderTemplateVars(template: string, vars: TemplateVars): string {
  let out = template;
  for (const name of ALLOWED_VARS) {
    const value = renderVar(name, vars);
    if (value === null) continue;
    out = out.split(`{${name}}`).join(value);
  }
  return out;
}

/**
 * Chạy toàn bộ flow trên ctx THẬT → messages (đã điền) + deepLinks + cờ cảnh báo NẶNG (@SEC-ONB-01).
 * THUẦN: không LLM, không DB, không Date.now.
 *
 * 🔴 US-AI-0298-S01 — `trace` TUỲ CHỌN (mặc định `NOOP_TRACE`): đường gọi cũ giữ NGUYÊN chữ ký 3 tham
 * số và hành vi. Có collector ⇒ phát bước `flow_run` tại chỗ flow THỰC SỰ chạy — không suy diễn từ
 * output ở tầng trên.
 */
/**
 * 🔴🔴 US-AI-0436-S02 (@UC9680 · @gate money) — THAM SỐ THEO TỪNG KHOÁ, KHÔNG PHẢI MỘT TÚI PHẲNG.
 *
 * BUG ĐÃ ĐO: bản cũ trả **một** `collectionId` cho **hai** route khác nghĩa —
 * `PAY_COLLECTION` (*đợt tôi cần đóng*) và `COLLECTION_BOARD` (*đợt tôi cần xác nhận*) — nối bằng
 * `firstDue?.collectionId ?? ctx.pendingConfirmationCollectionId`. Toán tử `??` khiến due **luôn
 * thắng**, kể cả trong flow xác nhận ⇒ nút "Xác nhận tại đây" dẫn tới đợt thu của **quỹ đã bị
 * `@AI-AMB-16` loại khỏi ngữ cảnh**, còn đợt thật sự có khoản chờ thì bị bỏ.
 *
 * `??` bắc cầu giữa hai nguồn **khác nghĩa** là lỗi thiết kế, không phải lỗi giá trị: nó biến "thiếu
 * dữ liệu của việc A" thành "lấy dữ liệu của việc B". Nay mỗi khoá đọc ĐÚNG nguồn của nó; thiếu ⇒
 * `undefined` ⇒ `resolveRoute` trả `null` ⇒ **bỏ nút** (fail-visible, cơ chế có sẵn ở
 * `route-catalog.ts:227-229` — trước đây chưa được dùng đúng).
 *
 * ⚠️ Bảng này là **closed-set và ĐƯỢC CANH BẰNG TEST TỰ KHÁM PHÁ**: `step-runner.spec.ts` duyệt MỌI
 * khoá trong `ROUTE_CATALOG` và đòi khoá có tham số phải khai đủ nguồn ở đây. Thêm khoá thứ 12 mà
 * quên khai ⇒ ĐỎ ngay, thay vì một nút chết im lặng.
 */
export const ROUTE_PARAM_SOURCES: Readonly<
  Record<string, Readonly<Record<string, (ctx: OrchestrationContext) => string | null | undefined>>>
> = Object.freeze({
  /** Đợt tôi cần ĐÓNG — chỉ từ khoản nợ được kể tên (S01: hợp lệ + `> 0`). */
  PAY_COLLECTION: Object.freeze({
    collectionId: (ctx: OrchestrationContext) => selectPrimaryDue(ctx)?.collectionId,
  }),
  /** Đợt tôi cần XÁC NHẬN — **và CHỈ** nguồn này. Không mượn due. */
  COLLECTION_BOARD: Object.freeze({
    collectionId: (ctx: OrchestrationContext) => ctx.pendingConfirmationCollectionId,
  }),
  /** Nhắc người chưa đóng của đợt đang xử lý — cùng nguồn với board. */
  COLLECTION_REMIND: Object.freeze({
    collectionId: (ctx: OrchestrationContext) => ctx.pendingConfirmationCollectionId,
  }),
  VOTE_PROPOSAL: Object.freeze({
    proposalId: (ctx: OrchestrationContext) => ctx.openVoteProposalId,
  }),
  // 🔴 GIỮ NGUYÊN luật US-AI-0302: MỘT quỹ ⇒ dùng; NHIỀU quỹ ⇒ BỎ TRỐNG ⇒ bỏ nút.
  // Lý do viết ngay tại chỗ vá cũ: *"chọn 1 trong N quỹ CŨNG LÀ ĐOÁN"*. Ba khoá dưới đều là lối vào
  // màn GHI TIỀN hoặc xuất báo cáo của MỘT quỹ ⇒ đoán sai quỹ là đoán sai đường tiền.
  EXPENSE_CREATE: Object.freeze({ fundId: singleFundId }),
  COLLECTION_CREATE: Object.freeze({ fundId: singleFundId }),
  REPORT_EXPORT: Object.freeze({ fundId: singleFundId }),
});

/** MỘT quỹ ⇒ id của nó; 0 hoặc ≥2 ⇒ `undefined` (đừng đoán — US-AI-0302). */
function singleFundId(ctx: OrchestrationContext): string | undefined {
  return ctx.funds.length === 1 ? ctx.funds[0]!.id : undefined;
}

/**
 * Tham số route của RIÊNG một `deepLinkKey`, rút TỪ ctx THẬT.
 *
 * Khoá không có trong bảng (kể cả khoá không-tham-số như `GROUP_CREATE`, và khoá ngoài catalog) ⇒ `{}`
 * ⇒ `resolveRoute` tự quyết: route không cần tham số thì giải được, route cần thì trả `null` (bỏ nút).
 * @AI-AMB-16: chỉ lấy từ ctx, KHÔNG bịa id.
 */
export function routeParamsFor(
  key: string,
  ctx: OrchestrationContext,
): Readonly<Record<string, string | null | undefined>> {
  const sources = ROUTE_PARAM_SOURCES[key];
  if (!sources) return Object.freeze({});
  const out: Record<string, string | null | undefined> = {};
  for (const [param, read] of Object.entries(sources)) out[param] = read(ctx);
  return Object.freeze(out);
}

/**
 * 🔴 US-AI-0436-S03 (@UC9681) — VIỆC theo TỪNG NGHĨA VỤ cho flow nợ.
 *
 * `MEMBER_HAS_DUE` là flow duy nhất mà **một lần khớp = N việc**: nợ 2 quỹ là hai việc khác nhau, hai
 * số tiền, hai đợt, hai nút. Mọi flow còn lại là 1 việc/1 lần khớp, nên chúng đi nhánh mặc định và
 * **hình dạng output không đổi một chữ**.
 *
 * Thứ tự = thứ tự `ctx.memberDues` (loader đã `orderBy [createdAt, id]`). 🔴 KHÔNG sort theo số tiền:
 * *"nợ nhiều hơn thì quan trọng hơn"* là phán đoán sản phẩm chưa ai quyết, và nó làm thứ tự thẻ NHẢY
 * mỗi lần người dùng trả bớt một khoản.
 */
function dueUnits(
  flow: Flow,
  ctx: OrchestrationContext,
  template: string,
  trace: TraceCollector,
  // 🔴 US-AI-0439-S02: cần `locale` để lấy đúng câu `detail`/`next`/nhãn CTA từ catalog.
  locale: "vi" | "en",
): readonly FlowUnit[] {
  const messageStep = flow.steps.find((st) => st.type === "message");
  const linkStep = flow.steps.find((st) => st.type === "deep_link" && st.deepLinkKey);
  const units: FlowUnit[] = [];

  for (const due of duesForFlow(flow, ctx)) {
    const fund = ctx.funds.find((f) => f.id === due.fundId);
    if (!fund) continue; // đã lọc ở `positiveValidDues`, giữ vế này cho type-narrowing
    const group = ctx.groups.find((g) => g.id === fund.groupId);
    const vars = {
      tenNguoiDung: safeDisplayName(ctx),
      tenQuy: fund.name,
      soTien: due.amountMinor,
      tenDot: due.periodLabel,
    };
    // 🔴 US-AI-0438-S03 (@UC9687) — quỹ của CHÍNH khoản này bị khoá ⇒ **bỏ NÚT, giữ THẺ**, và đổi câu
    // sang thể THÔNG BÁO. Giục "Đóng ngay" vào quỹ đang đóng băng = dẫn thẳng tới 409, đúng lúc nhóm
    // ở trạng thái nhạy cảm nhất. Bỏ cả thẻ thì tệ hơn: khoản nợ biến mất mà không ai giải thích.
    const locked = isScopeLockedForWrite(ctx, due.fundId);
    const message = locked
      ? locale === "en"
        ? lockedNoticeEn(fund.name)
        : lockedNoticeVi(fund.name)
      : messageStep
        ? renderStep(messageStep, template, vars)
        : "";
    // Nút của CHÍNH khoản này — không dùng `routeParamsFor` (nó trả khoản ĐƯỢC KỂ TÊN, tức khoản đầu).
    const link =
      !locked && linkStep?.deepLinkKey
        ? resolveRoute(linkStep.deepLinkKey, { collectionId: due.collectionId })
        : null;
    if (linkStep?.deepLinkKey && !link) {
      trace.emit({
        stage: "flow_run",
        actor: "ai",
        decision: locked
          ? `Bỏ lối đi "${linkStep.deepLinkKey}": quỹ/nhóm đang bị khoá ghi (nút sẽ dẫn tới 409).`
          : `Bỏ lối đi "${linkStep.deepLinkKey}" cho một khoản nợ: thiếu id đợt thu.`,
        codes: ["DEEPLINK_SKIPPED", linkStep.deepLinkKey, ...(locked ? ["SCOPE_LOCKED"] : [])],
      });
    }
    units.push(
      Object.freeze({
        message,
        // 🔴 MERGE 0438×0439: truyền ĐÚNG cờ khoá. Để `false` ở đây thì thẻ của quỹ đang bị đóng băng
        // vẫn khai `ctaless: "informational"` — nói với người dùng "chỉ để biết" trong khi sự thật là
        // "đang bị chặn". Cùng dữ liệu, sai lý do, và không test nào của riêng 0438 hay 0439 bắt được.
        ...enrichUnit(flow, ctx, locale, link, locked, vars),
        ...(group ? { groupId: group.id, groupName: group.name } : {}),
      }),
    );
  }
  return Object.freeze(units);
}

/**
 * 🔴 US-AI-0438-S03 (@UC9687) — PHẠM VI ĐANG BỊ KHOÁ GHI?
 *
 * Quỹ `frozen` ⇒ mọi WRITE tiền 409 `FUND_FROZEN`; nhóm `suspended` ⇒ `FundAccessGuard` freeze nghiệp
 * vụ. Cả hai đều **chặn WRITE, không chặn READ**.
 *
 * Không xác định được quỹ (vd `COLLECTION_REMIND` chỉ có `collectionId`) ⇒ chỉ coi là khoá khi **TOÀN
 * BỘ** nhóm trong ctx đang bị đình chỉ. Fail-closed hẹp có chủ đích: chặn oan một nút ở nhóm còn hoạt
 * động cũng là dựng ngõ cụt, chỉ theo hướng ngược lại.
 *
 * Vắng `status` (dữ liệu cũ / caller chưa nạp) ⇒ coi như ACTIVE ⇒ 0 regress.
 */
function isScopeLockedForWrite(ctx: OrchestrationContext, fundId: string | undefined): boolean {
  const fund = fundId ? ctx.funds.find((f) => f.id === fundId) : undefined;
  if (fund) {
    if (fund.status === "frozen") return true;
    return ctx.groups.find((g) => g.id === fund.groupId)?.status === "suspended";
  }
  return ctx.groups.length > 0 && ctx.groups.every((g) => g.status === "suspended");
}

/** Quỹ mà một CTA GHI sẽ tác động — dùng để hỏi `isScopeLockedForWrite`. */
function targetFundIdOf(key: string, ctx: OrchestrationContext): string | undefined {
  if (key === "PAY_COLLECTION") return selectPrimaryDue(ctx)?.fundId;
  if (key === "EXPENSE_CREATE" || key === "COLLECTION_CREATE") return singleFundId(ctx);
  return undefined; // COLLECTION_REMIND: ctx không mang quỹ của đợt đang nhắc.
}

/** Câu chữ thay thế khi phạm vi bị khoá: THỂ THÔNG BÁO, không giục hành động không làm được. */
function lockedNoticeVi(fundName: string): string {
  return `${fundName} đang tạm khoá — bạn chưa nộp được lúc này.`;
}

function lockedNoticeEn(fundName: string): string {
  return `${fundName} is temporarily locked — you cannot pay right now.`;
}

/**
 * 🔴 US-AI-0439-S02 (@UC9689) — phần GIÀU của một thẻ việc, lấy TỪ CATALOG.
 *
 * Ba nguyên tắc gói trong hàm này:
 *  1. **Không bịa câu.** Flow chưa khai `detailVi`/`nextVi` ⇒ trả về `undefined`, không có "câu mặc
 *     định tử tế" nào. Admin sửa copy ở catalog và app đổi theo — nếu tầng này tự nghĩ, admin sửa mà
 *     app vẫn nói câu cũ.
 *  2. **`next` chỉ có nghĩa với bậc `SETUP`** — người mới cần biết xong bước này thì tới đâu; người
 *     đang nợ không cần ai nhắc "bước kế".
 *  3. **CTA ưu tiên chip.** Chip chạy trong chat nên **luôn** dùng được; deep-link phụ thuộc màn đã
 *     dựng trên client (9/11 đích đang chết). Không có cả hai ⇒ khai `ctaless` **tường minh** thay vì
 *     im lặng — thẻ không nút mà không nói lý do thì người dùng tưởng app hỏng.
 */
function enrichUnit(
  flow: Flow,
  ctx: OrchestrationContext,
  locale: "vi" | "en",
  link: DeepLink | null,
  locked: boolean,
  /**
   * 🔴🔴 US-AI-0482-S01 (@UC9763) — `vars` DO CALLER ĐƯA, tham số BẮT BUỘC. Đọc kỹ trước khi "dọn".
   *
   * Cám dỗ hiển nhiên là gọi `buildTemplateVars(flow, ctx)` ngay trong hàm này cho gọn. LÀM THẾ SẼ
   * TÁI TẠO CHÍNH BUG ĐANG VÁ: với `MEMBER_HAS_DUE`, `buildTemplateVars` chỉ trả **khoản ĐẦU TIÊN**
   * (`rule-vars.ts` `duesForFlow(flow, ctx)[0]`) ⇒ nợ hai quỹ thì **cả hai** nút cùng thành
   * *"Đóng Quỹ lớp"*. Hai nút vẫn giống nhau, nhưng nay **giống nhau một cách nghe có vẻ đúng** —
   * tệ hơn hiện trạng, vì người dùng đọc tên quỹ trên nút rồi bấm nhầm.
   *
   * Nhánh nợ (`dueUnits`) đưa `vars` của **khoản đang lặp**; nhánh còn lại đưa `buildTemplateVars`.
   * `item-per-obligation.spec.ts` ca `AC-0482-3` khoá đúng cái bẫy này.
   */
  vars: TemplateVars,
): Partial<FlowUnit> {
  const cfg = flow.configurable;
  const detail = locale === "en" ? cfg.detailEn : cfg.detailVi;
  const next = cfg.band === "SETUP" ? (locale === "en" ? cfg.nextEn : cfg.nextVi) : undefined;
  const rawCtaLabel = locale === "en" ? cfg.ctaLabelEn : cfg.ctaLabelVi;
  const ctaLabel =
    rawCtaLabel === undefined ? undefined : renderTemplateVars(rawCtaLabel, vars);
  const chipStep = flow.steps.find((st) => st.chipPrompt);
  const chipPrompt =
    chipStep?.chipPrompt === undefined ? undefined : renderTemplateVars(chipStep.chipPrompt, vars);

  // 🔴 MERGE 0438 × 0439 — PHẠM VI KHOÁ THÌ KHÔNG CÓ CTA NÀO CẢ, kể cả chip.
  //
  // Chip là lối thoát cho deep-link chết, nhưng nó KHÔNG phải lối thoát cho một phạm vi đang bị chặn:
  // câu chip chạy trong chat rồi vẫn đâm vào cùng bức tường ở bước chốt (`FUND_FROZEN` 409). Bỏ nút
  // deep-link mà vẫn mời bấm chip là **dời ngõ cụt xuống sâu hơn**, sau khi người dùng đã điền form.
  const cta: FlowUnit["cta"] = locked
    ? undefined
    : chipPrompt
      ? { kind: "chip", label: ctaLabel ?? chipPrompt, prompt: chipPrompt }
      : link
        ? { kind: "deep_link", label: ctaLabel ?? link.labelVi, deepLink: link }
        : undefined;

  return {
    ...(detail === undefined ? {} : { detail }),
    ...(next === undefined ? {} : { next }),
    ...(link ? { deepLink: link } : {}),
    // Lý do vắng nút: catalog khai trước (US-AI-0440-S04), rồi mới tới suy luận của tầng trình bày.
    ...(cta
      ? { cta }
      : {
          ctaless:
            cfg.ctalessReason ?? (locked ? ("blocked" as const) : ("informational" as const)),
        }),
  };
}

export function runFlow(
  flow: Flow,
  ctx: OrchestrationContext,
  locale: "vi" | "en" = "vi",
  trace: TraceCollector = NOOP_TRACE,
): FlowOutput {
  const vars = buildTemplateVars(flow, ctx);
  // 🔴 US-AI-0443-S02 (@UC9703) — LẦN ĐẦU TUYỆT ĐỐI đổi CÂU CHỮ, không đổi VIỆC.
  //
  // Chọn template ở đây (tầng chạy flow) thay vì đẻ flow song sinh: nhân đôi catalog nghĩa là mỗi
  // lần sửa luật phải nhớ sửa hai chỗ, và sớm muộn chỉ sửa một. Flow không khai câu lần-đầu ⇒ rơi
  // về câu thường, 0 regress.
  const firstRun = ctx.isFirstRun === true;
  const template =
    locale === "en"
      ? (firstRun ? flow.configurable.templateFirstRunEn : undefined) ?? flow.configurable.templateEn
      : (firstRun ? flow.configurable.templateFirstRunVi : undefined) ?? flow.configurable.templateVi;

  let units: readonly FlowUnit[];
  if (flow.trigger.rule === "MEMBER_HAS_DUE") {
    units = dueUnits(flow, ctx, template, trace, locale);
  } else {
    const messages: string[] = [];
    const deepLinks: DeepLink[] = [];
    // Có bước GHI nào bị khoá phạm vi không — quyết định `ctaless: "blocked"` của thẻ (0438 × 0439).
    let anyWriteLocked = false;
    for (const step of flow.steps) {
      if (step.type === "message") {
        messages.push(renderStep(step, template, vars));
        continue;
      }
      if (!step.deepLinkKey) continue;
      // 🔴 US-AI-0295-S02 — giải route TỪ CATALOG với tham số THẬT của ctx.
      // Thiếu tham số ⇒ `resolveRoute` trả null ⇒ **BỎ nút**. Thà không có nút còn hơn nút dẫn tới màn
      // lỗi — đó chính là bug đang vá (7/9 route cũ không tồn tại).
      // 🔴 US-AI-0438-S03 (@UC9687) — CTA GHI trong phạm vi bị khoá ⇒ bỏ nút TRƯỚC khi giải route.
      // Dùng đúng cơ chế fail-visible sẵn có (bỏ nút + phát trace), KHÔNG dựng đường bỏ-nút thứ hai.
      const writeLocked =
        (WRITE_DEEPLINK_KEYS as readonly string[]).includes(step.deepLinkKey) &&
        isScopeLockedForWrite(ctx, targetFundIdOf(step.deepLinkKey, ctx));
      if (writeLocked) anyWriteLocked = true;
      const link = writeLocked
        ? null
        : resolveRoute(step.deepLinkKey, routeParamsFor(step.deepLinkKey, ctx));
      if (link) deepLinks.push(link);
      else {
        trace.emit({
          stage: "flow_run",
          actor: "ai",
          decision: writeLocked
            ? `Bỏ lối đi "${step.deepLinkKey}": quỹ/nhóm đang bị khoá ghi (nút sẽ dẫn tới 409).`
            : `Bỏ lối đi "${step.deepLinkKey}": thiếu dữ liệu để dựng đường dẫn (không đưa user tới màn lỗi).`,
          codes: ["DEEPLINK_SKIPPED", step.deepLinkKey, ...(writeLocked ? ["SCOPE_LOCKED"] : [])],
        });
      }
    }
    // Flow 1-việc: gộp mọi câu + nút vào MỘT unit ⇒ `messages`/`deepLinks` suy ra y hệt bản cũ.
    const primaryGroup = ctx.groups.length === 1 ? ctx.groups[0] : undefined;
    units = Object.freeze([
      Object.freeze({
        message: messages.join(" ").trim(),
        ...enrichUnit(flow, ctx, locale, deepLinks[0] ?? null, anyWriteLocked, vars),
        ...(primaryGroup ? { groupId: primaryGroup.id, groupName: primaryGroup.name } : {}),
      }),
    ]);
    // Flow có >1 nút (không có trong catalog hôm nay) ⇒ giữ nguyên mọi nút ở `deepLinks` phía dưới.
    if (deepLinks.length > 1) {
      units = Object.freeze([
        Object.freeze({ message: messages.join(" ").trim(), deepLink: deepLinks[0] }),
        ...deepLinks.slice(1).map((d) => Object.freeze({ message: "", deepLink: d })),
      ]);
    }
  }

  const messagesOut = units.map((u) => u.message).filter((m) => m.length > 0);
  const deepLinksOut = units.flatMap((u) => (u.deepLink ? [u.deepLink] : []));
  trace.emit({
    stage: "flow_run",
    actor: "ai",
    decision: `Chạy flow ${flow.id}: ${messagesOut.length} câu, ${deepLinksOut.length} lối đi.`,
    codes: [flow.code, flow.id],
  });
  return Object.freeze({
    code: flow.code,
    flowId: flow.id,
    units,
    messages: Object.freeze(messagesOut),
    deepLinks: Object.freeze(deepLinksOut),
    requiresHeavyConfirm: flow.configurable.requiresHeavyConfirm === true,
  });
}
